import {
    DeleteEssentialContractResponse,
    DeleteEssentialCurrencyResponse,
    DeleteEssentialOptionContractResponse,
    EssentialContractsResponse,
    EssentialCurrenciesResponse,
    EssentialOptionContract,
    EssentialOptionContractsResponse,
    HydrateContractsResponse,
    InsertBulkContractsResponse,
    InsertBulkCurrenciesResponse,
    InsertBulkOptionContractsResponse,
} from "../types/essentialInstruments";
import httpService from "./httpService";

/**
 * Service for Essential Instruments API operations.
 * All endpoints use the /v2/admin prefix (JWT auth).
 */
class EssentialInstrumentsService {
  private readonly baseUrl = "/v2/admin";

  // ============================================
  // Essential Contracts (Stock/Equity)
  // ============================================

  /**
   * Get all essential contract IDs.
   */
  async getEssentialContracts(): Promise<EssentialContractsResponse> {
    const response = await httpService.get<EssentialContractsResponse>(
      `${this.baseUrl}/essential-contracts`
    );
    return response.data;
  }

  /**
   * Bulk insert essential contract IDs (skips duplicates).
   */
  async insertEssentialContracts(contractIds: number[]): Promise<InsertBulkContractsResponse> {
    const response = await httpService.post<InsertBulkContractsResponse>(
      `${this.baseUrl}/essential-contracts`,
      { contractIds }
    );
    return response.data;
  }

  /**
   * Delete a single essential contract by ID.
   */
  async deleteEssentialContract(contractId: number): Promise<DeleteEssentialContractResponse> {
    const response = await httpService.delete<DeleteEssentialContractResponse>(
      `${this.baseUrl}/essential-contracts/${contractId}`
    );
    return response.data;
  }

  /**
   * Hydrate essential contracts from basket/portfolio stocks.
   */
  async hydrateEssentialContracts(): Promise<HydrateContractsResponse> {
    const response = await httpService.post<HydrateContractsResponse>(
      `${this.baseUrl}/essential-contracts/hydrate`
    );
    return response.data;
  }

  // ============================================
  // Essential Option Contracts
  // ============================================

  /**
   * Get all essential option contracts.
   */
  async getEssentialOptionContracts(): Promise<EssentialOptionContractsResponse> {
    const response = await httpService.get<EssentialOptionContractsResponse>(
      `${this.baseUrl}/essential-option-contracts`
    );
    return response.data;
  }

  /**
   * Bulk insert essential option contracts (skips duplicates).
   */
  async insertEssentialOptionContracts(
    contracts: EssentialOptionContract[]
  ): Promise<InsertBulkOptionContractsResponse> {
    const response = await httpService.post<InsertBulkOptionContractsResponse>(
      `${this.baseUrl}/essential-option-contracts`,
      { contracts }
    );
    return response.data;
  }

  /**
   * Delete a single essential option contract by contractId and exchange.
   */
  async deleteEssentialOptionContract(
    contractId: number,
    exchange: string
  ): Promise<DeleteEssentialOptionContractResponse> {
    const response = await httpService.delete<DeleteEssentialOptionContractResponse>(
      `${this.baseUrl}/essential-option-contracts/${contractId}/${encodeURIComponent(exchange)}`
    );
    return response.data;
  }

  // ============================================
  // Essential Currencies
  // ============================================

  /**
   * Get all essential currencies.
   */
  async getEssentialCurrencies(): Promise<EssentialCurrenciesResponse> {
    const response = await httpService.get<EssentialCurrenciesResponse>(
      `${this.baseUrl}/essential-currencies`
    );
    return response.data;
  }

  /**
   * Bulk insert essential currencies (skips duplicates).
   */
  async insertEssentialCurrencies(currencies: string[]): Promise<InsertBulkCurrenciesResponse> {
    const response = await httpService.post<InsertBulkCurrenciesResponse>(
      `${this.baseUrl}/essential-currencies`,
      { currencies }
    );
    return response.data;
  }

  /**
   * Delete a single essential currency by code.
   */
  async deleteEssentialCurrency(currency: string): Promise<DeleteEssentialCurrencyResponse> {
    const response = await httpService.delete<DeleteEssentialCurrencyResponse>(
      `${this.baseUrl}/essential-currencies/${encodeURIComponent(currency)}`
    );
    return response.data;
  }
}

export const essentialInstrumentsService = new EssentialInstrumentsService();
export default essentialInstrumentsService;
